/**
 * CatchUpButton — Library/Vault header pill that lets the user clear every "✨ N NEW"
 * badge across all series in one tap. Triggers a self-contained CSS confetti
 * burst from the click position, no extra deps.
 *
 * Props:
 *   - totalNew: number — sum of all unseen counts (button hidden if 0)
 *   - onCatchUp: async () => number — fires the "mark all seen" call,
 *     resolves with how many series were actually marked
 *   - variant: 'library' (default, fuchsia) | 'vault' (crimson)
 *   - haptic: bool — fire navigator.vibrate(50) on click (defaults true)
 *   - label: string — button label (default 'Catch up')
 */
import React, { useState, useRef, useCallback } from 'react';
import { Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';

const VARIANTS = {
  library: {
    btn: 'bg-gradient-to-br from-fuchsia-600 via-pink-600 to-rose-600 hover:from-fuchsia-500 hover:via-pink-500 hover:to-rose-500 border-fuchsia-300/50 shadow-[0_0_12px_-2px_rgba(217,70,239,0.55)] hover:shadow-[0_0_18px_-2px_rgba(217,70,239,0.75)]',
    text: 'text-fuchsia-50',
    confetti: ['#a78bfa', '#f472b6', '#fbbf24', '#34d399', '#60a5fa', '#f87171'],
  },
  vault: {
    btn: 'bg-gradient-to-br from-rose-700 via-red-700 to-fuchsia-800 hover:from-rose-600 hover:via-red-600 hover:to-fuchsia-700 border-rose-400/50 shadow-[0_0_12px_-2px_rgba(244,63,94,0.6)] hover:shadow-[0_0_18px_-2px_rgba(244,63,94,0.8)]',
    text: 'text-rose-50',
    confetti: ['#f43f5e', '#e11d48', '#a21caf', '#fda4af', '#fbbf24', '#fb923c'],
  },
};

const CatchUpButton = ({ totalNew = 0, onCatchUp, variant = 'library', haptic = true, label = 'Catch up' }) => {
  const v = VARIANTS[variant] || VARIANTS.library;
  const [busy, setBusy] = useState(false);
  const [bursts, setBursts] = useState([]);
  const burstIdRef = useRef(0);

  const fireConfetti = useCallback((originX, originY) => {
    const id = ++burstIdRef.current;
    // 32 particles per burst — enough to feel celebratory, light on perf.
    const particles = Array.from({ length: 32 }).map((_, i) => {
      const angle = (Math.PI * 2 * i) / 32 + (Math.random() - 0.5) * 0.3;
      const velocity = 80 + Math.random() * 110; // px
      return {
        id: i,
        dx: Math.cos(angle) * velocity,
        dy: Math.sin(angle) * velocity - 40, // slight upward bias
        rot: Math.random() * 720 - 360,
        color: v.confetti[i % v.confetti.length],
        size: 6 + Math.random() * 6,
        delay: Math.random() * 80,
      };
    });
    setBursts((b) => [...b, { id, x: originX, y: originY, particles }]);
    // Cleanup after animation finishes (1.4s + max delay).
    setTimeout(() => {
      setBursts((b) => b.filter((x) => x.id !== id));
    }, 1700);
  }, [v.confetti]);

  const handleClick = useCallback(async (e) => {
    if (busy || !onCatchUp) return;
    setBusy(true);
    // Capture click position for confetti origin.
    const rect = e.currentTarget.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    try {
      const result = await onCatchUp();
      // Backwards-compat: result may be a number (legacy) or { marked, bonus_coins }.
      const marked = typeof result === 'number' ? result : (result?.marked || 0);
      const bonus = typeof result === 'number' ? 0 : (result?.bonus_coins || 0);
      if (marked > 0) {
        fireConfetti(x, y);
        if (haptic && typeof navigator !== 'undefined' && navigator.vibrate) {
          try { navigator.vibrate(50); } catch {/* noop */}
        }
        const seriesLabel = `${marked} series`;
        const bonusLabel = bonus > 0 ? ` · +${bonus} coins 🪙` : '';
        toast.success(
          `Caught up on ${seriesLabel} 🎉${bonusLabel}`,
          { duration: 3000 }
        );
      }
    } finally {
      setBusy(false);
    }
  }, [busy, onCatchUp, fireConfetti, haptic]);

  if (totalNew <= 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        data-testid={`${variant}-catch-up-btn`}
        title={`Mark all ${totalNew} new ${totalNew === 1 ? 'item' : 'items'} as seen`}
        className={`group relative inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold ${v.text} px-2.5 sm:px-3 py-1.5 rounded-full ${v.btn} border transition-all active:scale-95 disabled:opacity-60`}
      >
        <Sparkles className="w-3.5 h-3.5 opacity-90 group-hover:rotate-12 transition-transform" />
        <span>{label}</span>
        <span
          className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-white/25 rounded-full"
          data-testid={`${variant}-catch-up-count`}
        >
          {totalNew > 99 ? '99+' : totalNew}
        </span>
      </button>

      {/* Confetti layer — fixed, pointer-events-none, only renders during a burst. */}
      {bursts.length > 0 && (
        <div className="fixed inset-0 pointer-events-none z-[60]" aria-hidden="true">
          {bursts.map((burst) => (
            <div key={burst.id} style={{ position: 'absolute', left: burst.x, top: burst.y }}>
              {burst.particles.map((p) => (
                <span
                  key={p.id}
                  className="catchup-confetti-piece"
                  style={{
                    '--dx': `${p.dx}px`,
                    '--dy': `${p.dy}px`,
                    '--rot': `${p.rot}deg`,
                    background: p.color,
                    width: `${p.size}px`,
                    height: `${p.size * 0.4}px`,
                    animationDelay: `${p.delay}ms`,
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      <style>{`
        .catchup-confetti-piece {
          position: absolute;
          left: 0;
          top: 0;
          border-radius: 1px;
          opacity: 0;
          animation: catchup-confetti 1.4s cubic-bezier(0.2, 0.7, 0.3, 1) forwards;
          will-change: transform, opacity;
        }
        @keyframes catchup-confetti {
          0%   { transform: translate(0, 0) rotate(0deg); opacity: 1; }
          70%  { opacity: 1; }
          100% { transform: translate(var(--dx), calc(var(--dy) + 220px)) rotate(var(--rot)); opacity: 0; }
        }
      `}</style>
    </>
  );
};

export default CatchUpButton;
